import { NotAffectingLineHeightObject } from '@upright/ui/common/generic/components/not-affecting-line-height-object';
import { FC, ReactElement, SVGProps } from 'react';
import { Text } from '../typography';
import { TextContext, Provider, DEFAULT_SLOT } from 'react-aria-components';
import { isElement } from 'react-is';

import styles from './Input.module.css';

export type Adornment = string | number | FC<SVGProps<SVGSVGElement>> | ReactElement;

export type Props = {
  children: Adornment;
};

export function InputAdornment({ children: Children }: Props) {
  if (isElement(Children)) {
    return (
      <div className={styles.adornment}>
        <NotAffectingLineHeightObject>{Children}</NotAffectingLineHeightObject>
      </div>
    );
  }

  const isText = typeof Children === 'string' || typeof Children === 'number';
  const adornmentProps = isText ? undefined : { 'aria-hidden': true };

  /*
   * A plain <div>, NOT react-aria's <Label>. Inside a `TextField`, every `Label` receives the
   * *same* generated id (the one the input's `aria-labelledby` points at), so an adorned field
   * rendered two or three elements carrying one id: axe `duplicate-id-aria`, and an accessible
   * name resolved from whichever duplicate the browser happened to match first — e.g. a
   * currency field announcing itself as "EUR" instead of "Revenue". Adornments are decoration
   * next to the field, so they must not claim to label it.
   */
  return (
    <div {...adornmentProps} className={styles.adornment}>
      <Provider
        values={[
          [
            TextContext,
            {
              slots: {
                [DEFAULT_SLOT]: {},
              },
            },
          ],
        ]}
      >
        <Text color="gray-600">
          {isText ? (
            Children
          ) : (
            <NotAffectingLineHeightObject>
              <Children width="1.125rem" height="1.125rem" />
            </NotAffectingLineHeightObject>
          )}
        </Text>
      </Provider>
    </div>
  );
}
